import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ChartLineUp, Sparkle, Briefcase, ChartBar } from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";
import NewsletterSignup from "../components/NewsletterSignup";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Landing() {
  const nav = useNavigate();
  const { user, loading } = useAuth();
  const [busy, setBusy] = useState(false);

  const signIn = () => {
    setBusy(true);
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (!loading && user) {
    // already signed in
    setTimeout(() => nav("/dashboard", { replace: true }), 0);
  }

  return (
    <div className="min-h-screen bg-paper text-ink">
      {/* Top bar */}
      <header className="border-b border-black/10">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-ikb text-paper grid place-items-center font-display font-bold">S</div>
            <span className="font-display text-lg">StockProfit Life</span>
          </div>
          <button onClick={signIn} disabled={busy} className="btn-primary text-sm" data-testid="landing-signin-top">
            {busy ? "Redirecting…" : "Sign in with Google"}
            <ArrowRight size={14} />
          </button>
        </div>
      </header>

      {/* Hero: split 50/50 */}
      <section className="border-b border-black/10">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2">
          <div className="p-8 lg:p-16 flex flex-col justify-center">
            <div className="label-eyebrow mb-6">Issue N°01 · Retail Investor Edition</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-[0.95] tracking-tight">
              Stocks,<br />
              <span className="text-ikb">explained</span> like<br />
              you own the<br />
              business.
            </h1>
            <p className="mt-8 text-lg text-ink2 max-w-lg leading-relaxed">
              Every ticker hides a business. StockProfit Life breaks down profit margins, revenue and risk in plain English —
              then tracks your holdings live so you always know where you stand.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <button onClick={signIn} disabled={busy} className="btn-primary" data-testid="landing-signin-cta">
                {busy ? "Redirecting…" : "Continue with Google"}
                <ArrowRight size={16} />
              </button>
              <span className="text-xs text-ink2 font-mono-num">FREE · NO CREDIT CARD</span>
            </div>
          </div>

          <div className="relative border-l border-black/10 bg-paper2 grain min-h-[420px] lg:min-h-full overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1768055104929-cf2317674a80?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxwZXJzb24lMjBsb29raW5nJTIwYXQlMjBzdG9jayUyMGNoYXJ0cyUyMHRhYmxldHxlbnwwfHx8fDE3ODM3NDAzMzB8MA&ixlib=rb-4.1.0&q=85"
              alt="Trader analyzing markets"
              className="absolute inset-0 w-full h-full object-cover mix-blend-multiply opacity-95"
            />
            <div className="absolute bottom-6 left-6 right-6 bg-paper border border-black/10 p-5">
              <div className="label-eyebrow">Featured breakdown</div>
              <div className="mt-2 font-display text-2xl">NVDA · Gross Margin</div>
              <div className="mt-3 flex items-end gap-4">
                <div className="font-mono-num text-4xl font-medium text-ikb">75.1%</div>
                <div className="text-xs text-pos font-mono-num pb-2">+1.9pp YoY</div>
              </div>
              <div className="mt-3 h-2 bg-paper2 relative">
                <div className="absolute inset-y-0 left-0 bg-ikb" style={{ width: "75%" }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bento features */}
      <section className="max-w-[1400px] mx-auto px-6 py-16">
        <div className="label-eyebrow mb-3">Section 02</div>
        <h2 className="font-display text-3xl sm:text-4xl mb-10 max-w-2xl">Four tools. One goal — you actually understand what you own.</h2>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-0 border border-black/10">
          {[
            { icon: <Sparkle weight="fill" size={24} />, tint: "text-coral", title: "AI Explainer", body: "Ask Claude anything about a stock. Get plain-English answers grounded in real fundamentals." },
            { icon: <ChartBar weight="fill" size={24} />, tint: "text-ikb", title: "Profit Margins", body: "See gross, operating & net margins side by side — the truest signal of business quality." },
            { icon: <ChartLineUp weight="fill" size={24} />, tint: "text-mint", title: "Live Watchlist", body: "Track tickers you care about. Prices, day change and margin health in one dense table." },
            { icon: <Briefcase weight="fill" size={24} />, tint: "text-violet2", title: "Portfolio P&L", body: "Add holdings, see unrealized gains, and know your true portfolio-wide profit margin." },
          ].map((f, i) => (
            <div key={i} className={`p-8 ${i < 3 ? "border-r border-black/10" : ""} ${i < 2 ? "md:border-r md:border-black/10" : ""} border-b md:border-b-0 border-black/10 hover:bg-paper2 transition-colors`}>
              <div className={`${f.tint}`}>{f.icon}</div>
              <div className="font-display text-xl mt-4">{f.title}</div>
              <p className="text-sm text-ink2 leading-relaxed mt-2">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-black/10 bg-ink text-paper">
        <div className="max-w-[1400px] mx-auto px-6 py-12 grid md:grid-cols-3 gap-10">
          <div>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-paper text-ink grid place-items-center font-display font-bold text-sm">S</div>
              <span className="font-display text-lg">StockProfit Life</span>
            </div>
            <p className="text-sm text-paper/70 mt-3 max-w-xs">Understand the businesses behind the tickers. Track their margins. Track your P&amp;L.</p>
          </div>
          <div>
            <div className="label-eyebrow" style={{ color: "rgba(249,248,246,0.6)" }}>Weekly brief</div>
            <div className="font-display text-2xl mt-2 mb-4">One email. Zero noise.</div>
            <p className="text-sm text-paper/70 mb-4">A short read every Sunday: margin movers, quiet earnings, and one deep-dive.</p>
            <NewsletterSignup source="landing_footer" theme="dark" />
          </div>
          <div>
            <div className="label-eyebrow" style={{ color: "rgba(249,248,246,0.6)" }}>Legal</div>
            <ul className="mt-2 space-y-1 text-sm text-paper/70">
              <li>Educational use only — not financial advice.</li>
              <li>Market data via Alpha Vantage.</li>
              <li>© 2026 StockProfit Life</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-paper/10">
          <div className="max-w-[1400px] mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-3 text-xs text-paper/60 font-mono-num">
            <span>© 2026 STOCKPROFIT LIFE · EDUCATIONAL USE ONLY</span>
            <button onClick={signIn} className="text-paper hover:text-amber2 transition-colors" data-testid="landing-signin-footer">
              Sign in →
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}
